import { Link } from 'react-router-dom';
import { useTranslation } from '../../i18n/useTranslation';
import type { TimelineEvent, TimelineEventType } from '../../types/timeline';
import { Text } from '../atoms/Typography/Text';
import { formatTimelineRelativeTime, timelineEventHref, timelineEventTitle } from './timelineEventText';
import './TimelineEventCard.css';

const TYPE_COLOR: Record<TimelineEventType, string> = {
  icp_analysis: 'var(--color-status-info)',
  measurement: 'var(--color-status-success)',
  journal_entry: 'var(--color-secondary)',
  image: 'var(--color-accent)',
  task: 'var(--color-status-warning)',
  dosing: 'var(--color-chart-ca)',
  livestock: 'var(--color-chart-mg)',
  ai_insight: 'var(--color-status-ai)',
};

interface TimelineEventCardProps {
  event: TimelineEvent;
  /** The aquarium the event belongs to (builds the link target). */
  aquariumId: string;
}

export function TimelineEventCard({ event, aquariumId }: TimelineEventCardProps) {
  const { t, i18n } = useTranslation();
  const borderColor = TYPE_COLOR[event.type] ?? 'var(--color-border-primary)';
  const title = timelineEventTitle(event, t);

  return (
    <Link
      to={timelineEventHref(event, aquariumId)}
      className="timeline-event-card"
      style={{ borderLeftColor: borderColor }}
      aria-label={title}
    >
      <div className="timeline-event-card__content">
        <div className="timeline-event-card__header">
          <span className="timeline-event-card__title">{title}</span>
          {event.hasAiInsight && (
            <span className="timeline-event-card__ai-badge" title={t('timeline.aiInsight')}>
              {t('dashboard.timeline.aiBadge')}
            </span>
          )}
        </div>
        {event.summary != null && (
          <Text className="timeline-event-card__summary">{event.summary}</Text>
        )}
        <span className="timeline-event-card__time">
          {formatTimelineRelativeTime(event.occurredAt, i18n.language)}
        </span>
      </div>
      {event.thumbnailUrl != null && (
        <div className="timeline-event-card__thumbnail">
          <img
            src={event.thumbnailUrl}
            alt={title}
            width={56}
            height={56}
            className="timeline-event-card__thumbnail-img"
          />
        </div>
      )}
    </Link>
  );
}
